import { matchPath } from "react-router-dom-v5-compat";
import { PATH_HOME, PATH_TRIPS } from "@hopper-b2b/utilities";

import type { HtsAssistEntryPoint } from "./htsAssistEntryTypes";

export type { HtsAssistEntryPoint } from "./htsAssistEntryTypes";

// Trips detail-route product segment -> entry-point surface, so opening the FAB
// on a booking's detail page reports that surface instead of the generic "trips"
const TRIP_DETAILS_ENTRY_POINT_BY_ROUTE_SEGMENT = {
  activities: "activities-trip-details",
  flights: "flights-trip-details",
  hotels: "hotels-trip-details",
  cars: "cars-trip-details",
  homes: "homes-trip-details",
  packages: "packages-trip-details",
} as const;

/**
 * Originating source surface an HTS Assist conversation started from — the page
 * analogue of `entryAction` (what the user did vs. where they were). Namespaced
 * with "b2bportal" on the wire (e.g. "b2bportal-mobile-chat") to satisfy the
 * generate-token `entryPoint` contract (non-empty, <=64 chars, charset
 * `^[A-Za-z0-9_.:-]+$`). Values are the RBC surfaces that can start a chat (FAB
 * on home / trips / a booking's trip-details, mobile webview), named after the
 * RBC route segments rather than commerce-portals' product names.
 */
const ENTRY_POINT_SLUG_PREFIX = "b2bportal-";
const ENTRY_POINT_SLUG_MAX_LENGTH = 64;

/**
 * Namespaced slug sent on the generate-token request (e.g. "b2bportal-mobile-chat").
 * Also accepts raw pathnames from `pathnameToEntryPointWithFallback` (e.g.
 * "/flights/search" -> "b2bportal-flights-search"): rbc-bff rejects the whole
 * request with a 400 if the slug breaks the contract, so it is sanitized here.
 */
export const toEntryPointSlug = (
  entryPoint: HtsAssistEntryPoint | string
): string => {
  const surface =
    entryPoint
      .replace(/[^A-Za-z0-9_.:-]+/g, "-")
      .replace(/-{2,}/g, "-")
      .replace(/^-|-$/g, "") || "unknown";
  return `${ENTRY_POINT_SLUG_PREFIX}${surface}`
    .slice(0, ENTRY_POINT_SLUG_MAX_LENGTH)
    .replace(/-$/, "");
};

/**
 * Maps the current portal pathname to the FAB's entry-point surface. A booking
 * detail route (`trips/{flights|hotels|cars|homes|packages}/:reservationId`)
 * resolves to its `*-trip-details` surface (leaf wins), the rest of `trips/*`
 * to "trips", and the root to "home" (matched exactly since `PATH_HOME` is "/"
 * and would otherwise prefix-match every route). Returns `undefined` elsewhere.
 */
export const pathnameToEntryPoint = (
  pathname: string
): HtsAssistEntryPoint | undefined => {
  const detailMatch = matchPath(
    { path: `${PATH_TRIPS}:productSegment/:reservationId`, end: false },
    pathname
  );
  const productSegment = detailMatch?.params.productSegment;
  if (
    productSegment &&
    productSegment in TRIP_DETAILS_ENTRY_POINT_BY_ROUTE_SEGMENT
  ) {
    return TRIP_DETAILS_ENTRY_POINT_BY_ROUTE_SEGMENT[
      productSegment as keyof typeof TRIP_DETAILS_ENTRY_POINT_BY_ROUTE_SEGMENT
    ];
  }

  if (matchPath({ path: PATH_TRIPS, end: false }, pathname)) return "trips";
  if (matchPath({ path: PATH_HOME, end: true }, pathname)) return "home";
  return undefined;
};

export const pathnameToEntryPointWithFallback = (
  pathname: string
): HtsAssistEntryPoint | string => pathnameToEntryPoint(pathname) ?? pathname;

/**
 * The floating launcher opens the embedded lobby on home/search surfaces.
 * `/trips` owns the page variant of the lobby, so its launcher opens regular
 * Assist instead. Trip-detail routes also continue through direct chat.
 */
export const shouldOpenHtsAssistLobby = (pathname: string): boolean => {
  const entryPoint = pathnameToEntryPoint(pathname);
  return !entryPoint || entryPoint === "home";
};
